import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IconLock, IconWorld } from '@tabler/icons-react';
import { NoteVisibility } from '@/types/notes';
import styles from './VisibilityPills.module.css';

const ICON_SIZE = 16;

const VISIBILITY_CSS: Record<NoteVisibility, string> = {
  [NoteVisibility.Private]: styles.private ?? '',
  [NoteVisibility.Workspace]: styles.workspace ?? '',
};

interface VisibilityPillsProps {
  value: NoteVisibility;
  onChange: (visibility: NoteVisibility) => void;
}

const VisibilityPillsComponent = ({
  value,
  onChange,
}: VisibilityPillsProps) => {
  const { t } = useTranslation();

  const visibilityConfig = useMemo(
    () => ({
      [NoteVisibility.Private]: {
        label: t('notes.visibility.private'),
        icon: <IconLock size={ICON_SIZE} />,
      },
      [NoteVisibility.Workspace]: {
        label: t('notes.visibility.workspace'),
        icon: <IconWorld size={ICON_SIZE} />,
      },
    }),
    [t]
  );

  return (
    <div className={styles.visibilityPills}>
      {Object.values(NoteVisibility).map((visibility) => {
        const config = visibilityConfig[visibility];
        const isSelected = value === visibility;

        return (
          <button
            key={visibility}
            type="button"
            className={`${styles.visibilityPill} ${VISIBILITY_CSS[visibility]} ${isSelected ? styles.selected : ''}`}
            onClick={() => onChange(visibility)}
            aria-pressed={isSelected}
            aria-label={`${t('notes.form.visibilityLabel')}: ${config.label}${isSelected ? ` (${t('accessibility.selected')})` : ''}`}
          >
            <div className={styles.pillContent}>
              <span className={styles.icon}>{config.icon}</span>
              {config.label}
            </div>
          </button>
        );
      })}
    </div>
  );
};

export const VisibilityPills = memo(VisibilityPillsComponent);
